import { getDistance, getDistanceAlongPath } from '@m1/geo-utils';
import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import ChartJs from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartZoom from 'chartjs-plugin-zoom';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import 'chartjs-plugin-dragdata';
import React, { useRef, useMemo } from 'react';
import { RiAddLine, RiSubtractLine } from 'react-icons/ri';
import { useSelector, useDispatch } from 'react-redux';

import styles from './index.module.scss';

import actions from '@/actions';
import EmptyList from '@/components/ui/EmptyList';
import { getCommandForSurveySegment } from '@/helpers/CommandConverter';
import { getCSSVariable, getColorWithOpacity } from '@/utils/theme';

const cx = classNames.bind(styles);

const Items = () => {
  const dispatch = useDispatch();
  const commands = useSelector((state) => state.mission.json.commands);
  const collisions = useSelector((state) => state.mission.collisions);
  const elevations = useSelector((state) => state.mission.elevations);
  const path = useSelector((state) => state.editor.path);
  const chartRef = useRef();
  const chart = useRef();
  const range = { min: useRef(), max: useRef() };

  const waypoints = useMemo(() => {
    const draftWaypoints = [];
    let labelNumber = 1;

    commands.forEach((command) => {
      switch (command.type) {
        case 'navLand':
          draftWaypoints.push({ ...command, label: labelNumber });

          // Parachute 항목 체크한 경우
          if (command.data.parachute) {
            labelNumber += 3; // Waypoint, Parachute Enable, Release 반영
          }
          break;

        case 'navLoiterTime':
        case 'navLoiterToAlt':
        case 'navLoiterTurns':
        case 'navTakeoff':
          draftWaypoints.push({ ...command, label: labelNumber });
          break;

        case 'navWaypoint':
          draftWaypoints.push({ ...command, label: labelNumber });

          // Speed 항목 체크한 경우
          if (command.data.speed) {
            labelNumber++; // Change Speed 반영
          }
          break;

        case 'navReturnToLaunch':
          draftWaypoints.push({ ...command, label: labelNumber, data: commands[0].data });
          break;

        case 'cusSurvey':
          {
            const surveyCommand = getCommandForSurveySegment(command);
            const startLabel = labelNumber;
            const endLabel = labelNumber + surveyCommand.data.positions.length - 1;

            // 서베이 시작점
            draftWaypoints.push({
              ...command,
              id: `${command.id}/start`,
              label: startLabel,
              data: {
                position: surveyCommand.data.positions.at(0),
                altitude: command.data.altitude,
              },
            });

            // 서베이 종료점
            draftWaypoints.push({
              ...command,
              id: `${command.id}/end`,
              label: endLabel,
              data: {
                position: surveyCommand.data.positions.at(-1),
                altitude: command.data.altitude,
              },
            });

            labelNumber = endLabel;
          }
          break;

        default:
          break;
      }

      labelNumber++;
    });

    return draftWaypoints;
  }, [commands]);

  const hasCollision = useMemo(() => {
    return collisions.some((collision) => collision !== null);
  }, [collisions]);

  useMountEffect(() => {
    ChartJs.register(ChartZoom);
    ChartJs.register(ChartDataLabels);
  });

  useUpdateEffect(() => {
    drawChart();
  }, [collisions, elevations]);

  const drawChart = () => {
    const chartData = getChartData();

    const mslAltitudes = waypoints.map(({ data }) => waypoints[0].data.elevation + data.altitude);
    const minValue = Math.ceil(Math.min(...elevations, ...mslAltitudes));
    const maxValue = Math.ceil(Math.max(...elevations, ...mslAltitudes));
    const margin = (maxValue - minValue) * 0.1; // 10% of the difference
    range.min.current = minValue - margin;
    range.max.current = maxValue + margin;

    // 차트 존재 시
    if (chart.current) {
      chart.current.data.labels = chartData.labels;
      chart.current.data.datasets[0].data = chartData.datasets[0].data;
      chart.current.data.datasets[1].data = chartData.datasets[1].data;
      chart.current.data.datasets[2].data = chartData.datasets[2].data;
      chart.current.options.scales.yAxis.min = range.min.current;
      chart.current.options.scales.yAxis.max = range.max.current;
      chart.current.options.plugins.dragData.onDragStart = handleDragStart;
      chart.current.options.plugins.dragData.onDragEnd = handleDragEnd;
      chart.current.update();
    }
    // 차트 미존재 시
    else {
      const context = chartRef.current.getContext('2d');
      const options = {
        type: 'line',
        data: chartData,
        options: {
          layout: {
            padding: {
              top: 4,
              right: 12,
              bottom: 12,
              left: 8,
            },
          },
          maintainAspectRatio: false,
          scales: {
            xAxis: { display: false },
            yAxis: {
              min: range.min.current,
              max: range.max.current,
              grid: { color: 'rgba(255, 255, 255, 0.1)' },
              ticks: {
                font: { size: 12 },
                callback: (value) => `${commaNumber(Math.trunc(value))}m`,
              },
            },
          },
          onHover: (e, points) => {
            if (points.length && points[0].datasetIndex === 0) {
              const point = e.chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, false);
              if (point.length) {
                e.native.target.style.cursor = 'grab';
                return;
              }
            }
            e.native.target.style.cursor = 'default';
          },
          plugins: {
            zoom: { zoom: { mode: 'y' } },
            dragData: {
              round: 1,
              onDragStart: handleDragStart,
              onDrag: handleDrag,
              onDragEnd: handleDragEnd,
            },
            tooltip: {
              callbacks: {
                title: () => null, // Tooltip 제목 제거
              },
            },
            legend: false,
          },
        },
      };

      chart.current = new ChartJs(context, options);
    }
  };

  const handleDragStart = (e, datasetIndex, index, value) => {
    // Collision, Elevation 선택 시 drag 방지
    if ([1, 2].includes(datasetIndex)) return false;
    // Survey drag 방지
    if (waypoints[index].type === 'cusSurvey') return false;
    // RTL drag 방지
    if (waypoints[index].type === 'navReturnToLaunch') return false;
  };

  const handleDrag = (e, datasetIndex, index, value) => {
    e.target.style.cursor = 'grabbing';
  };

  const handleDragEnd = (e, datasetIndex, index, value) => {
    e.target.style.cursor = 'default';

    const targetIndex = commands.findIndex(({ id }) => id === waypoints[index].id);
    const altitude = value.y - waypoints[0].data.elevation;
    dispatch(actions.mission.editWaypoint(targetIndex, 'altitude', altitude));
  };

  const getChartData = () => {
    // 총 거리
    const wholeDistance = getDistanceAlongPath(path.map(({ position }) => position));
    // 누적 거리
    let accumulatedDistance = 0;

    return {
      labels: elevations.map((_, index) => index + 1),
      datasets: [
        {
          label: 'MSL Altitude',
          data: waypoints.map((waypoint, index) => {
            let x = 0;
            if (index > 0) {
              const from = waypoints[index - 1].data.position;

              if (waypoint.type === 'cusSurvey' && waypoint.id.includes('/end')) {
                const commandId = waypoint.id.split('/')[0];
                const command = commands.find(({ id }) => id === commandId);
                const surveyCommand = getCommandForSurveySegment(command);
                accumulatedDistance += getDistanceAlongPath(surveyCommand.data.positions);
              } else {
                const to = waypoint.data.position;
                accumulatedDistance += getDistance(from, to);
              }

              if (wholeDistance > 0) {
                x = Math.round((accumulatedDistance / wholeDistance) * elevations.length);
              }
            }

            let y = waypoints[0].data.elevation;
            // Land 경우
            if (waypoint.type === 'navLand') {
              // 직전 경로점 고도 동일
              y += waypoints[index - 1].data.altitude;
            } else {
              y += waypoint.data.altitude;
            }

            return { x, y, label: waypoint.label };
          }),
          fill: true,
          borderColor: getCSSVariable('--tint-color'),
          pointStyle: 'circle',
          borderWidth: 1,
          pointBorderWidth: 10,
          pointBackgroundColor: getCSSVariable('--tint-color'),
          backgroundColor: getColorWithOpacity(getCSSVariable('--tint-color'), 0.2),
          datalabels: {
            display: true,
            color: 'white',
            font: { size: '9px' },
          },
        },
        {
          label: 'Elevation',
          data: elevations.map((elevation, index) => ({
            x: index + 1,
            y: elevation,
          })),
          fill: true,
          borderColor: 'gold',
          borderWidth: 1,
          pointBorderWidth: 0,
          backgroundColor: '#ffd70022',
          datalabels: { display: false },
        },
        {
          label: 'Collision',
          data: collisions.map((collision, index) => ({
            x: index + 1,
            y: collision,
          })),
          fill: true,
          borderColor: 'orangered',
          borderWidth: 1,
          pointBorderWidth: 0,
          backgroundColor: '#ff450022',
          datalabels: { display: false },
        },
      ],
    };
  };

  const zoomOut = () => {
    const { yAxis } = chart.current.scales;

    const allValues = [
      ...chart.current.data.datasets[0].data.map(({ y }) => y),
      ...chart.current.data.datasets[1].data.map(({ y }) => y),
    ];
    const minValue = Math.ceil(Math.min(...allValues));
    const maxValue = Math.ceil(Math.max(...allValues));
    const pixelOfGap = yAxis.getPixelForValue(minValue) - yAxis.getPixelForValue(maxValue);

    // Only when exceeding 40px
    if (40 < pixelOfGap) {
      chart.current.zoom({ y: 0.8 }, 300);
    }
  };

  const zoomIn = () => {
    const { yAxis } = chart.current.scales;

    if (yAxis.min < range.min.current && range.max.current < yAxis.max) {
      chart.current.zoom({ y: 1.2 }, 300);
    }
  };

  return (
    <div className={cx('container')}>
      <div className={cx('header')}>
        <div className={cx('title')}>Elevation</div>
        <div className={cx('legends')}>
          <div className={cx('legend')}>
            <div className={cx('color', 'mslAltitude')}></div>
            <div className={cx('label')}>MSL Altitude</div>
          </div>
          <div className={cx('legend')}>
            <div className={cx('color', 'elevation')}></div>
            <div className={cx('label')}>Elevation</div>
          </div>
          {hasCollision && (
            <div className={cx('legend')}>
              <div className={cx('color', 'collision')}></div>
              <div className={cx('label')}>Collision</div>
            </div>
          )}
        </div>
        <div className={cx('buttons')}>
          <div className={cx('button')} onClick={zoomOut}>
            <RiSubtractLine size={16} color="white" />
          </div>
          <div className={cx('button')} onClick={zoomIn}>
            <RiAddLine size={16} color="white" />
          </div>
        </div>
      </div>
      <div className={cx('body')}>
        {commands.length === 0 && (
          <div className={cx('empty')}>
            <EmptyList message="No items" />
          </div>
        )}
        <div className={cx(['chartWrapper', { show: commands.length > 0 }])}>
          <canvas ref={chartRef} className={cx('canvas')}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Items;
